import moment from 'moment'
import { Logger } from '~/plugins/log'

const logger = new Logger()
logger.setContext('Protect')

const getDefaultState = () => ({
  token: null,
  subscriber: {},
  pk: null,
  invoices: [],
  upcomingInvoice: null,
  address: {},
  messages: [],
  messageTimeout: 5 * 1000,
  hasBeenLoggedOut: false
})

export const state = getDefaultState

export const mutations = {
  SET_SUBSCRIBER (state, { pk, token, data }) {
    state.pk = pk
    state.token = token
    state.subscriber = { ...data }
    state.hasBeenLoggedOut = false

    if (data.addresses) {
      state.address = data.addresses.sort((a, b) => a.id - b.id)[data.addresses.length - 1]
    }
  },
  LOGOUT (state) {
    Object.assign(state, getDefaultState())
  },
  SET_INVOICES (state, { invoices, upcomingInvoice }) {
    state.invoices = invoices
    state.upcomingInvoice = upcomingInvoice
  },
  SET_ADDRESS (state, address) {
    state.address = address
  },
  ADD_MESSAGE (state, body) {
    state.messages = [{
      type: 'success',
      body,
      timestamp: (new Date()).valueOf()
    }, ...state.messages]
  },
  ADD_ERROR_MESSAGE (state, body) {
    state.messages = [{
      type: 'error',
      body,
      timestamp: (new Date()).valueOf()
    }, ...state.messages]
  },
  CLEAN_MESSAGES (state) {
    state.messages = state.messages.filter(message => message.timestamp > (new Date()).valueOf() - state.messageTimeout)
  },
  MARK_HAS_BEEN_LOGGED_OUT (state) {
    state.hasBeenLoggedOut = true
  }
}

export const getters = {
  subscriber: state => state.subscriber || {},
  authenticated: state => state.token,
  items: (_, getters) => [...(getters.subscriber.valid_items || [])].sort(
    (a, b) => (b.protected ? 1 : -1) - (a.protected ? 1 : -1)
  ),
  protectedItems: (_, getters) => getters.items.filter(item => item.protected),
  unprotectedItems: (_, getters) => getters.items.filter(item => !item.protected),
  customer: (_, getters) => getters.subscriber.customer || {},
  subscription: (_, getters) => getters.subscriber.subscription || {},
  orders: (_, getters) => getters.subscriber.orders || [],
  invoice: (_, getters) => {
    if (getters.subscriber.invoice && getters.subscriber.invoice.id) return getters.subscriber.invoice
    if (getters.subscriber.latest_invoice && getters.subscriber.latest_invoice.id) return getters.subscriber.latest_invoice
    return {}
  },
  token: state => state.token,
  pk: state => state.pk,
  cardRequiresAction: (_, getters) => ['requires_payment_method', 'requires_action'].includes((getters.invoice.payment_intent || {}).status),
  isSubscribed: (_, getters) => {
    // No items protected
    if (!getters.protectedItems) return false

    // No subscription
    if (!getters.subscription.id) return false

    // Subscription has ended
    if (moment(getters.subscription.current_period_end) < moment()) return false

    // Subscription has been cancelled
    if (getters.subscription.status === 'canceled') return false

    // Subscription is incomplete
    if (getters.subscription.status === 'incomplete') return false

    return true
  },
  isPastDue: (_, getters) => {
    return getters.subscription.status === 'past_due'
  },
  newPrice: (state, getters) => itemIds => getters.items.filter(
    item => itemIds.includes(item.original_item_id)
  ).reduce((sum, item) => sum + item.price, 0),
  card: (_, getters) => (getters.customer.default_payment_method || {}).card || {},
  cardBrandIcon: (_, getters) => {
    return {
      visa: 'fa-cc-visa',
      mastercard: 'fa-cc-mastercard',
      amex: 'fa-cc-amex',
      discover: 'fa-cc-discover',
      diners: 'fa-cc-diners-club',
      jcb: 'fa-cc-jcb',
      unknown: 'fa-credit-card'
    }[getters.card.brand] || 'fa-credit-card'
  },
  invoices: state => state.invoices,
  upcomingInvoice: state => state.upcomingInvoice,
  item: (_, getters) => itemId => getters.items.find(item => item.original_item_id === parseInt(itemId)),
  lrkSku: (state, getters, rootState, rootGetters) => sku => `${rootGetters['product/baseSkuFromPart']((sku || '').split('_')[0])}lrk-${sku}`,
  address: state => state.address,
  openExchange: (_, getters) => (itemId) => {
    const matchingOrders = [...getters.orders].filter(
      order => order.item === itemId && !(order.returned || order.waived || order.cancelled || order.paid_off)
    ).sort(
      (a, b) => b.orderid.localeCompare(a.orderid)
    )

    if (matchingOrders) return matchingOrders[0]
    return null
  },
  messages: state => (state.messages || []).sort(
    (a, b) => b.timestamp - a.timestamp
  ),
  messageTimeout: state => state.messageTimeout,
  hasBeenLoggedOut: state => state.hasBeenLoggedOut
}

export const actions = {
  async GET_SUBSCRIBER ({ commit }, { pk, token }) {
    logger.debug('GET_SUBSCRIBER')

    if (!pk || !token) return false

    try {
      const { data } = await this.$publicAPI.get(`protect/${pk}/?token=${token}`)
      commit('SET_SUBSCRIBER', { pk, token, data })
      return true
    } catch (error) {
      if (!error.response || parseInt(error.response.status) !== 404) {
        // Errors are only interesting if they're not 404
        this.app.$sentry.captureException(error)
        logger.error(error)
      }
      commit('LOGOUT')
      commit('MARK_HAS_BEEN_LOGGED_OUT')
      return false
    }
  },

  async REFRESH ({ commit, getters }) {
    logger.debug('REFRESH')

    try {
      const { data } = await this.$publicAPI.get(`protect/${getters.pk}/?token=${getters.token}`)
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return true
    } catch (error) {
      if (!error.response || parseInt(error.response.status) !== 404) {
        // Errors are only interesting if they're not 404
        this.app.$sentry.captureException(error)
        logger.error(error)
      }
      commit('MARK_HAS_BEEN_LOGGED_OUT')
      commit('LOGOUT')
      return false
    }
  },

  async CREATE_SUBSCRIPTION ({ commit, getters }, { method, itemIds }) {
    logger.debug('CREATE_SUBSCRIPTION')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/create_subscription/?token=${getters.token}`, {
        method: method.id,
        item_ids: itemIds
      })
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to save subscription' } }
    }
  },

  async RETRY_INVOICE ({ commit, getters }, { customer, method, invoice }) {
    logger.debug('RETRY_INVOICE')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/retry_invoice/?token=${getters.token}`, {
        method: method.id,
        invoice_id: invoice.id,
        customer_id: customer.id
      })
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to save subscription' } }
    }
  },

  async SYNC_WITH_STRIPE ({ commit, getters }) {
    logger.debug('SYNC_WITH_STRIPE')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/sync/?token=${getters.token}`)
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to save subscription' } }
    }
  },

  async UPDATE_SUBSCRIPTION ({ commit, getters }, { itemIds }) {
    logger.debug('UPDATE_SUBSCRIPTION')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/update_items/?token=${getters.token}`, {
        item_ids: itemIds
      })
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to save subscription' } }
    }
  },

  async CANCEL_SUBSCRIPTION ({ commit, getters }) {
    logger.debug('CANCEL_SUBSCRIPTION')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/cancel_subscription/?token=${getters.token}`)
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to cancel subscription' } }
    }
  },

  async GET_INVOICES ({ commit, getters }) {
    logger.debug('GET_INVOICES')

    try {
      const { data } = await this.$publicAPI.get(`protect/${getters.pk}/invoices/?token=${getters.token}`)
      commit('SET_INVOICES', {
        invoices: data.invoices,
        upcomingInvoice: data.upcoming_invoice
      })
      return true
    } catch (error) {
      if (!error.response || parseInt(error.response.status) !== 404) {
        // Errors are only interesting if they're not 404
        this.app.$sentry.captureException(error)
        logger.error(error)
      }
      return false
    }
  },

  async GET_SETUP_INTENT ({ getters }) {
    logger.debug('GET_SETUP_INTENT')

    try {
      const { data } = await this.$publicAPI.get(`protect/${getters.pk}/get_setup_intent/?token=${getters.token}`)
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to load card update form.' } }
    }
  },

  async PAY_OFF_ITEM ({ getters }, { itemId }) {
    logger.debug('PAY_OFF_ITEM')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/pay_off_item/?token=${getters.token}`, { item: itemId })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to pay off item.' } }
    }
  },
  
  async UPDATE_CARD ({ commit, getters }, { method }) {
    logger.debug('UPDATE_CARD')

    try {
      const { data } = await this.$publicAPI.post(`protect/${getters.pk}/update_card/?token=${getters.token}`, {
        method
      })
      commit('SET_SUBSCRIBER', { pk: getters.pk, token: getters.token, data })
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to update card.' } }
    }
  },

  async SAVE_ADDRESS ({ commit, getters }, body) {
    logger.debug('SAVE_ADDRESS')

    try {
      const { data } = await this.$publicAPI.post(
        `protect/${getters.pk}/save_address/?token=${getters.token}`,
        body
      )
      commit('SET_ADDRESS', data)
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      return { error: { message: 'Unable to save address.' } }
    }
  },

  async PLACE_ORDER ({ commit, getters }, body) {
    logger.debug('PLACE_ORDER')

    try {
      await this.$publicAPI.post(
        `protect/${getters.pk}/place_order/?token=${getters.token}`,
        body
      )
      return { success: true }
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
      
      if (error.response && error.response.data && error.response.data.detail) {
        return { error: { message: error.response.data.detail } }
      }

      return { error: { message: 'Unable to place order.' } }
    }
  },

  async SEND_LOGIN_LINK ({ commit, getters }, email) {
    logger.debug('SEND_LOGIN_LINK')

    try {
      await this.$publicAPI.post('protect/send_login_link/', { email })
      return { success: true }
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)

      return { error: { message: 'Unable to send login link. Please try again.' } }
    }
  }
}